import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { SelectionToolbar } from './SelectionToolbar';
import { toolbarButtons } from '../../config/orderStatusConfig';
import {
  BulkPreparationModal,
  BulkPreparationModalRef,
} from '../modals/BulkPreparationModal';
import { type LogisticStatusCounts } from '../../adapters/CountsApiAdapter';

export interface StatusToolbarProps {
  onStatusChange?: (status_id?: number) => void;
  selectedCount?: number;
  onClearSelection?: () => void;
  onCancelSelected?: () => void;
  onPrepareSelected?: (
    preparationType: 'picking_packing' | 'massive_packing'
  ) => void;
  onMarkForSendSelected?: () => void;
  onUncancelSelected?: () => void;
  // Contadores para cada acción (opcional, para mostrar cuántos elementos son elegibles para cada acción)
  cancelableCount?: number;
  preparableCount?: number;
  sendableCount?: number;
  uncancelableCount?: number;
  // Conteos de estados logísticos
  logisticStatusCounts?: LogisticStatusCounts;
  countsLoading?: boolean;
}

export const StatusToolbar = ({
  onStatusChange,
  selectedCount = 0,
  onClearSelection,
  onCancelSelected,
  onPrepareSelected,
  onMarkForSendSelected,
  onUncancelSelected,
  cancelableCount = 0,
  preparableCount = 0,
  sendableCount = 0,
  uncancelableCount = 0,
  logisticStatusCounts,
  countsLoading = false,
  active,
  setActive,
}: StatusToolbarProps & {
  active: string;
  setActive: (key: string) => void;
}) => {
  const { t } = useTranslation('ordersShippedModule');
  const bulkPreparationModalRef = useRef<BulkPreparationModalRef>(null);

  const handlePrepareSelected = () => {
    if (preparableCount > 0) {
      bulkPreparationModalRef.current?.open(preparableCount);
    }
  };

  const handleBulkPreparationConfirm = (
    preparationType: 'picking_packing' | 'massive_packing'
  ) => {
    onPrepareSelected?.(preparationType);
  };

  // Barra flotante solo en mobile y solo si hay selección
  const showSelectionToolbar = selectedCount > 0;
  const isMobileView =
    typeof window !== 'undefined' ? window.innerWidth < 768 : false;

  return (
    <>
      {/* Mobile: barra flotante */}
      {showSelectionToolbar && isMobileView && (
        <div>
          <SelectionToolbar
            selectedCount={selectedCount}
            onClose={onClearSelection || (() => {})}
            onCancelSelected={onCancelSelected || (() => {})}
            onPrepareSelected={handlePrepareSelected}
            onMarkForSendSelected={onMarkForSendSelected || (() => {})}
            onUncancelSelected={onUncancelSelected || (() => {})}
            cancelableCount={cancelableCount}
            preparableCount={preparableCount}
            sendableCount={sendableCount}
            uncancelableCount={uncancelableCount}
          />
        </div>
      )}

      {/* Desktop: barra de estados siempre visible */}
      <div
        className="flex bg-card-bg rounded-lg text-sm p-1 mb-4 overflow-x-auto scrollbar-hide light:border-card-border light:border"
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {toolbarButtons.map((btn) => {
          {
            /* Botones de filtro por estado logístico - Logistic status filter buttons */
          }
          const count =
            logisticStatusCounts?.[btn.key as keyof LogisticStatusCounts] || 0;

          return (
            <a
              key={btn.key}
              href={btn.link}
              onClick={(e) => {
                e.preventDefault();
                setActive(btn.key);
                if (onStatusChange) {
                  onStatusChange(btn.status_id);
                }
              }}
              className={`px-3 py-1 rounded hover:bg-hover-press whitespace-nowrap ${active === btn.key ? 'bg-hover-press' : ''}`}
            >
              {t(
                `toolbar.${btn.key.replace('in_preparation2', 'in_preparation')}`
              )}
              {!countsLoading && btn.key !== 'all' && ` (${count})`}
            </a>
          );
        })}
      </div>

      <BulkPreparationModal
        ref={bulkPreparationModalRef}
        onConfirm={handleBulkPreparationConfirm}
      />
    </>
  );
};
